import * as SDK from 'azure-devops-extension-sdk';
import { CommonServiceIds } from 'azure-devops-extension-api';
import { monday } from '../platformSpecific/monday';
import * as Sentry from '@sentry/react';
import jwt from 'jwt-decode';

// TODO check if this is needed
// import 'azure-devops-ui/Core/override.css';

export const JIRA = 'jira';
export const Azure = 'azure';
export const Monday = 'monday';

const getDeploymentTypeDescription = () => {
  const { type } = SDK.getHost();
  if (type === SDK.HostType.Deployment) return 'Deployment';
  if (type === SDK.HostType.Enterprise) return 'Enterprise';
  if (type === SDK.HostType.Organization) return 'Organization';
  return 'Unknown';
};

const initAzure = async () => {
  try {
    SDK.init({ loaded: false });
    await SDK.ready();
    SDK.notifyLoadSucceeded();
    const [token, projectService] = await Promise.all([
      SDK.getAppToken(),
      SDK.getService(CommonServiceIds.ProjectPageService),
    ]);
    const project = await projectService.getProject();
    return {
      id: Azure,
      token,
      isLicensed: true,
      context: {
        slug: SDK.getHost().name,
        accountId: SDK.getHost().id,
        type: getDeploymentTypeDescription(),
        boardId: project?.id,
        boardViewId: null, // boardViewId is only relevant for Monday
      },
    };
  } catch (error) {
    Sentry.captureException(error);
    throw new Error(`Azure SDK init failed:`, error);
  }
};

const initMonday = async () => {
  const [tokenResponse, contextResponse] = await Promise.all([
    monday.get('sessionToken'),
    monday.get('context'),
  ]);
  return {
    id: Monday,
    token: tokenResponse.data,
    isLicensed: true,
    context: {
      boardId: String(contextResponse.data.boardId),
      boardViewId: String(contextResponse.data.boardViewId),
    },
  };
};

const getJiraToken = async () => {
  return new Promise((resolve) => {
    AP.context.getToken((token) => {
      resolve(token);
    });
  });
};

const getJiraBaseUrl = async () => {
  return new Promise((resolve) => {
    AP.getLocation((location) => resolve(new URL(location).hostname));
  });
};

const initJira = async () => {
  const tokenResponse = await getJiraToken();
  const jiraBaseUrl = await getJiraBaseUrl();
  const decodedToken = jwt(tokenResponse);

  return {
    id: JIRA,
    token: tokenResponse,
    isLicensed: decodedToken.context.license?.active,
    context: {
      boardId: decodedToken.context.jira.project.id,
      boardKey: decodedToken.context.jira.project.key,
      boardViewId: null, // boardViewId is only relevant for Monday
      userId: decodedToken.sub,
      tenantId: decodedToken.iss,
      slug: jiraBaseUrl,
    },
  };
};

let hostPlatform;

export const HostPlatform = {
  get activeId() {
    if (HostPlatform.isAzure()) return Azure;
    if (HostPlatform.isMonday()) return Monday;
    if (HostPlatform.isJira()) return JIRA;

    throw new Error(
      `Unknown platform ID. Is the env variable REACT_APP_HOST_PLATFORM set correctly?`
    );
  },

  async init() {
    if (hostPlatform) return hostPlatform;
    if (HostPlatform.isAzure()) return (hostPlatform = await initAzure());
    if (HostPlatform.isMonday()) return (hostPlatform = await initMonday());
    if (HostPlatform.isJira()) return (hostPlatform = await initJira());
    throw new Error(
      `Unknown platform configuration. Is the env variable REACT_APP_HOST_PLATFORM set correctly? Currently it is: "${hostPlatform}"`
    );
  },

  get() {
    return hostPlatform;
  },

  getContext() {
    if (!hostPlatform)
      throw new Error("Host plataform hasn't been initialized yet.");
    return hostPlatform.context;
  },

  isMonday() {
    return process.env.REACT_APP_HOST_PLATFORM === 'monday';
  },

  isJira() {
    return process.env.REACT_APP_HOST_PLATFORM === 'jira';
  },

  isAzure() {
    return process.env.REACT_APP_HOST_PLATFORM === 'azure';
  },

  isLicensed() {
    return HostPlatform.get().isLicensed;
  },
};
